import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, KeyRound, Mail, Clock, AlertTriangle, Check, Copy, Send, RotateCw, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();

  // 2FA TOTP setup
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [copied, setCopied] = useState(false);

  // 2FA reset via email
  const [resetOpen, setResetOpen] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [resetSent, setResetSent] = useState(false);

  // Email OTP enable/disable
  const [emailToggleOpen, setEmailToggleOpen] = useState(false);
  const [emailToggleCode, setEmailToggleCode] = useState("");
  const [emailToggleSent, setEmailToggleSent] = useState(false);
  const [emailToggleMode, setEmailToggleMode] = useState("enable"); // enable | disable

  // Change password
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const startSetup = async () => {
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setSetupOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start 2FA setup");
    }
  };

  const confirmSetup = async () => {
    try {
      await api.post("/auth/2fa/verify", { code: verifyCode });
      toast.success("Authenticator 2FA enabled");
      setSetupOpen(false);
      setVerifyCode("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    }
  };

  // Reset 2FA flow: request email -> verify -> auth app cleared
  const openResetDialog = async () => {
    setResetOpen(true);
    setResetSent(false);
    setResetCode("");
    try {
      await api.post("/auth/2fa/reset/request");
      setResetSent(true);
      toast.success(`Verification code sent to ${user.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email. Is Email configured?");
      setResetOpen(false);
    }
  };

  const confirmReset = async () => {
    try {
      await api.post("/auth/2fa/reset/confirm", { code: resetCode });
      toast.success("Authenticator 2FA reset. You can enroll again now.");
      setResetOpen(false);
      setResetCode("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    }
  };

  const openEmailToggle = async (mode) => {
    setEmailToggleMode(mode);
    setEmailToggleOpen(true);
    setEmailToggleSent(false);
    setEmailToggleCode("");
    try {
      // Both enable and disable use the 'enable_email_otp' purpose for the OTP itself
      await api.post("/auth/email-otp/enable/request");
      setEmailToggleSent(true);
      toast.success(`Verification code sent to ${user.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email");
      setEmailToggleOpen(false);
    }
  };

  const confirmEmailToggle = async () => {
    try {
      if (emailToggleMode === "enable") {
        await api.post("/auth/email-otp/enable/confirm", { code: emailToggleCode });
        toast.success("Email OTP enabled");
      } else {
        await api.post("/auth/email-otp/disable", { code: emailToggleCode });
        toast.success("Email OTP disabled");
      }
      setEmailToggleOpen(false);
      setEmailToggleCode("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated");
      setCurrentPw(""); setNewPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(setupData?.secret || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-zinc-800/80">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            {user.is_admin && (
              <Link to="/admin" className="text-xs font-mono text-zinc-500 hover:text-zinc-200" data-testid="profile-admin-link">/admin</Link>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400 hover:text-zinc-100" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-10">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">// account</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-2" data-testid="profile-name">{user.name || user.email}</h1>
          <div className="mt-3 flex items-center gap-3 text-sm text-zinc-400">
            <Mail className="w-4 h-4" /> <span className="font-mono">{user.email}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-zinc-900 border border-zinc-800 font-mono uppercase">{user.auth_type}</span>
            {user.totp_enabled && <span className="px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-mono uppercase">app 2fa</span>}
            {user.email_otp_enabled && <span className="px-2 py-1 rounded bg-blue-500/10 border border-blue-500/20 text-blue-400 font-mono uppercase">email 2fa</span>}
            {user.is_admin && <span className="px-2 py-1 rounded bg-amber-500/10 border border-amber-500/20 text-amber-400 font-mono uppercase">admin</span>}
          </div>
        </div>

        <section className="space-y-3">
          <h2 className="font-display text-lg font-medium text-zinc-200">Security</h2>

          {/* Authenticator App 2FA */}
          <Card className="bg-zinc-900/60 border-zinc-800 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <Smartphone className="w-5 h-5 text-zinc-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium">Authenticator app (TOTP)</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {user.totp_enabled ? "Active — your authenticator app generates a 6-digit code at each sign-in." : "Use 1Password, Authy, Google Authenticator, or Microsoft Authenticator."}
                  </div>
                </div>
              </div>
              {user.auth_type === "password" ? (
                user.totp_enabled ? (
                  <Button variant="outline" size="sm" onClick={openResetDialog} className="border-zinc-700 hover:bg-zinc-800" data-testid="reset-2fa-btn">
                    <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Reset
                  </Button>
                ) : (
                  <Button size="sm" onClick={startSetup} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="setup-2fa-btn">Set up</Button>
                )
              ) : (
                <span className="text-xs text-zinc-500 font-mono">managed by entra</span>
              )}
            </div>
          </Card>

          {/* Email OTP */}
          <Card className="bg-zinc-900/60 border-zinc-800 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <Send className="w-5 h-5 text-zinc-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium">Email one-time codes</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {user.email_otp_enabled ? `Active — codes are sent to ${user.email} at sign-in.` : "Receive a 6-digit code at your email address as a 2FA alternative."}
                  </div>
                </div>
              </div>
              {user.auth_type === "password" ? (
                user.email_otp_enabled ? (
                  <Button variant="outline" size="sm" onClick={() => openEmailToggle("disable")} className="border-zinc-700 hover:bg-zinc-800" data-testid="disable-email-otp-btn">Disable</Button>
                ) : (
                  <Button size="sm" onClick={() => openEmailToggle("enable")} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="enable-email-otp-btn">Enable</Button>
                )
              ) : (
                <span className="text-xs text-zinc-500 font-mono">managed by entra</span>
              )}
            </div>
          </Card>

          {/* Change password */}
          {user.auth_type === "password" && (
            <Card className="bg-zinc-900/60 border-zinc-800 p-5">
              <div className="flex items-start gap-3 mb-4">
                <KeyRound className="w-5 h-5 text-zinc-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium">Change password</div>
                  <div className="text-xs text-zinc-500 mt-1">Minimum 8 characters. Use a passphrase for best security.</div>
                </div>
              </div>
              <form onSubmit={changePassword} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Current</Label>
                  <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" required data-testid="current-password-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">New</Label>
                  <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" required minLength={8} data-testid="new-password-input" />
                </div>
                <div className="sm:col-span-2">
                  <Button type="submit" size="sm" className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="change-password-btn">Update password</Button>
                </div>
              </form>
            </Card>
          )}
        </section>

        <section>
          <h2 className="font-display text-lg font-medium text-zinc-200 mb-3">Session</h2>
          <Card className="bg-zinc-900/60 border-zinc-800 p-5">
            <div className="flex items-center gap-3 text-sm text-zinc-400">
              <Clock className="w-4 h-4" />
              <span>You&apos;re signed in for up to 36 hours after your last activity.</span>
            </div>
            {user.last_login_at && <div className="mt-2 text-xs text-zinc-500 font-mono">Last login: {new Date(user.last_login_at).toLocaleString()}</div>}
          </Card>
        </section>
      </main>

      {/* 2FA Setup Dialog */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display">Set up authenticator</DialogTitle>
            <DialogDescription className="text-zinc-400">Scan the QR code, then enter the 6-digit code to confirm.</DialogDescription>
          </DialogHeader>
          {setupData && (
            <div className="space-y-4">
              <div className="bg-white p-3 rounded-md w-fit mx-auto" data-testid="2fa-qr-code">
                <img src={setupData.qr_data_url} alt="2FA QR" className="w-48 h-48" />
              </div>
              <div className="text-xs text-zinc-500 text-center">Or enter this secret manually:</div>
              <div className="flex items-center gap-2 justify-center">
                <code className="font-mono text-xs bg-zinc-950 border border-zinc-800 px-3 py-2 rounded select-all" data-testid="2fa-secret">{setupData.secret}</code>
                <button onClick={copySecret} className="p-2 rounded hover:bg-zinc-800" data-testid="copy-secret-btn">
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-zinc-400" />}
                </button>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Verification code</Label>
                <Input value={verifyCode} onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))} maxLength={6} className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-center text-xl tracking-[0.4em]" placeholder="000000" data-testid="2fa-verify-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={confirmSetup} disabled={verifyCode.length !== 6} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="2fa-confirm-btn">Enable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset 2FA Dialog (email OTP gated) */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><RotateCw className="w-5 h-5 text-amber-400" /> Reset authenticator 2FA</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Lost access to your authenticator app? We&apos;ve sent a 6-digit code to your email.
              Enter it below to remove your existing 2FA so you can enroll a new device.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-zinc-500 font-mono">{resetSent ? `Code sent to ${user.email}` : "Sending…"}</div>
          <Input value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ""))} maxLength={6} className="bg-zinc-950 border-zinc-800 font-mono text-center text-xl tracking-[0.4em]" placeholder="000000" data-testid="reset-2fa-code" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={confirmReset} disabled={resetCode.length !== 6} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reset-2fa-confirm">Confirm reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email OTP enable/disable dialog */}
      <Dialog open={emailToggleOpen} onOpenChange={setEmailToggleOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display">{emailToggleMode === "enable" ? "Enable" : "Disable"} email one-time codes</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Enter the 6-digit code we just sent to <span className="font-mono">{user.email}</span> to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-zinc-500 font-mono">{emailToggleSent ? "Code sent" : "Sending…"}</div>
          <Input value={emailToggleCode} onChange={(e) => setEmailToggleCode(e.target.value.replace(/\D/g, ""))} maxLength={6} className="bg-zinc-950 border-zinc-800 font-mono text-center text-xl tracking-[0.4em]" placeholder="000000" data-testid="email-otp-toggle-code" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailToggleOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={confirmEmailToggle} disabled={emailToggleCode.length !== 6} className={emailToggleMode === "enable" ? "bg-white text-zinc-950 hover:bg-zinc-200" : "bg-red-600 hover:bg-red-700 text-white"} data-testid="email-otp-toggle-confirm">
              {emailToggleMode === "enable" ? "Enable" : "Disable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
